/** Fuso oficial da plataforma MomyEdu (horario de parede da escola). */
export const FUSO_ESCOLA = 'America/Sao_Paulo';

const ISO_NAIVE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

/**
 * Interpreta datetime naive retornado pela API como horario de Brasilia.
 * Desde 2019 o Brasil nao usa horario de verao; America/Sao_Paulo e UTC-3 fixo.
 */
export function parseDateTimeApi(value: string): Date {
  const normalized = value.trim().replace(/\.\d{3}Z?$/, '').replace(/Z$/, '');
  const match = normalized.match(ISO_NAIVE);
  if (!match) {
    return new Date(value);
  }
  const [, year, month, day, hour, minute, second = '0'] = match;
  const padded = `${year}-${month}-${day}T${hour}:${minute}:${second.padStart(2, '0')}-03:00`;
  return new Date(padded);
}

export function formatDateTimeBr(value: string | Date): string {
  const date = typeof value === 'string' ? parseDateTimeApi(value) : value;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_ESCOLA,
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date).replace(', ', ' ');
}

export function formatDateTimeBrSegundos(value?: string | null): string {
  if (!value) return '—';
  const date = parseDateTimeApi(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_ESCOLA,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatTimeBr(value: string | Date): string {
  const date = typeof value === 'string' ? parseDateTimeApi(value) : value;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_ESCOLA,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatRelativeBr(value: string | Date, agora = Date.now()): string {
  const date = typeof value === 'string' ? parseDateTimeApi(value) : value;
  const diferencaSegundos = (date.getTime() - agora) / 1000;
  const absoluto = Math.abs(diferencaSegundos);
  if (absoluto < 60) return 'agora';
  const unidades: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60 * 60 * 24 * 365, 'year'],
    [60 * 60 * 24 * 30, 'month'],
    [60 * 60 * 24 * 7, 'week'],
    [60 * 60 * 24, 'day'],
    [60 * 60, 'hour'],
    [60, 'minute'],
  ];
  const [segundosDaUnidade, unidade] =
    unidades.find(([segundos]) => absoluto >= segundos) ?? [60, 'minute'];
  const valor = Math.floor(absoluto / segundosDaUnidade) * Math.sign(diferencaSegundos);
  return new Intl.RelativeTimeFormat('pt-BR', { numeric: 'always' }).format(valor, unidade);
}

export function hojeIsoBr(agora = new Date()): string {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO_ESCOLA,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(agora);
  const ano = partes.find((p) => p.type === 'year')?.value ?? '1970';
  const mes = partes.find((p) => p.type === 'month')?.value ?? '01';
  const dia = partes.find((p) => p.type === 'day')?.value ?? '01';
  return `${ano}-${mes}-${dia}`;
}

export function agoraHoraBr(agora = new Date()): string {
  const partes = new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_ESCOLA,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(agora);
  const hora = partes.find((p) => p.type === 'hour')?.value ?? '00';
  const minuto = partes.find((p) => p.type === 'minute')?.value ?? '00';
  return `${hora}:${minuto}`;
}

export function compareDateTimeApi(a: string, b: string): number {
  return parseDateTimeApi(a).getTime() - parseDateTimeApi(b).getTime();
}
